import { useMemo } from 'react';
import { Row } from '@tanstack/react-table';
import { startCase } from 'lodash-es';
import { DataTable, type DataTableColumnDef } from 'components/data-table';
import type {
  SlotsByFormationResponse,
  SlotsByFormationRow,
  SlotsByFormationBasin,
} from 'types/api-responses';
import { Toggle } from 'components/toggle/toggle';
import { useShowExpiredPermitsFormation } from 'components/slot-summary-table';
import {
  WELL_STATUS_COUNT_COLUMNS,
  WELL_STATUS_COUNT_KEYS,
  wellStatusCounts,
  type WellStatusCounts,
} from 'utils/slot-categories';
import styles from './slots-by-formation-table.module.scss';
// Each Create New Well status is its own column (WellStatusCounts, from the shared list).
interface SlotFormation
  extends Record<string, unknown>, Partial<WellStatusCounts> {
  formation: string;
  pdp?: number;
  duc?: number;
  permit?: number;
  activeEvalLTD?: number;
  valuedSlotTotal?: number;
  activeUpsideLTD?: number;
  shutIn?: number;
  inactive?: number;
  expiredPermit?: number;
  total?: number;
}

interface SlotFormationRow extends Record<string, unknown>, WellStatusCounts {
  _rowType: 'basin' | 'total';
  _basinName: string;
  _formations?: SlotFormation[];
  formation: string;
  pdp: number;
  duc: number;
  permit: number;
  activeEvalLTD: number;
  valuedSlotTotal: number;
  activeUpsideLTD: number;
  shutIn: number;
  inactive: number;
  expiredPermit: number;
  total: number;
}

/**
 * Format basin name to Title Case.
 * e.g., "MIDLAND" -> "Midland", "NO BASIN" -> "No Basin"
 *
 * @param {string} basin - The basin name to format.
 * @return {string} The formatted basin name.
 */
const formatBasinName = (basin: string): string => {
  if (!basin) return basin;
  return startCase(basin.toLowerCase());
};

/**
 * Calculate totals for a basin across all its formations.
 *
 * @param {SlotsByFormationRow[]} rows - The formation rows to sum.
 * @return {object} The summed totals for all numeric fields.
 */
const calculateBasinTotals = (rows: SlotsByFormationRow[]) => {
  return rows.reduce(
    (acc, row) => ({
      pdp: acc.pdp + (row.pdp ?? 0),
      duc: acc.duc + (row.duc ?? 0),
      permit: acc.permit + (row.permit ?? 0),
      activeEvalLTD: acc.activeEvalLTD + (row.activeEvalLTD ?? 0),
      valuedSlotTotal: acc.valuedSlotTotal + (row.valuedSlotTotal ?? 0),
      activeUpsideLTD: acc.activeUpsideLTD + (row.activeUpsideLTD ?? 0),
      shutIn: acc.shutIn + (row.shutIn ?? 0),
      inactive: acc.inactive + (row.inactive ?? 0),
      ...(Object.fromEntries(
        WELL_STATUS_COUNT_KEYS.map((key) => [key, acc[key] + (row[key] ?? 0)])
      ) as WellStatusCounts),
      expiredPermit: acc.expiredPermit + (row.expiredPermit ?? 0),
      total: acc.total + (row.total ?? 0),
    }),
    {
      pdp: 0,
      duc: 0,
      permit: 0,
      activeEvalLTD: 0,
      valuedSlotTotal: 0,
      activeUpsideLTD: 0,
      shutIn: 0,
      inactive: 0,
      ...wellStatusCounts(),
      expiredPermit: 0,
      total: 0,
    }
  );
};

// ─────────────────────────────────────
// Shared field config (columns + sub-table)
// ─────────────────────────────────────

// Base numeric fields (without expired permit - it's added conditionally based on toggle)
const BASE_NUMERIC_FIELDS = [
  { key: 'pdp', header: 'Producing' },
  { key: 'duc', header: 'DUC' },
  { key: 'permit', header: 'Permit' },
  { key: 'activeEvalLTD', header: 'Active Eval LTD' },
  { key: 'valuedSlotTotal', header: 'Valued Slot Total' },
  { key: 'shutIn', header: 'Shut In' },
  { key: 'inactive', header: 'Inactive' },
  ...WELL_STATUS_COUNT_COLUMNS.map(({ key, label }) => ({
    key,
    header: label,
  })),
  { key: 'activeUpsideLTD', header: 'Active Upside LTD' },
  { key: 'total', header: 'Total' },
] as const;

// Get numeric fields based on showExpiredPermits toggle
const getNumericFields = (showExpiredPermits: boolean) => {
  if (showExpiredPermits) {
    return [
      ...BASE_NUMERIC_FIELDS,
      { key: 'expiredPermit', header: 'Expired Permit' },
    ] as const;
  }
  return BASE_NUMERIC_FIELDS;
};

// ─────────────────────────────────────
// Column Definitions (parent table) - dynamic based on toggle
// ─────────────────────────────────────

const getColumns = (
  showExpiredPermits: boolean
): DataTableColumnDef<SlotFormationRow>[] => {
  const numericFields = getNumericFields(showExpiredPermits);
  return [
    {
      id: 'formation',
      accessorKey: 'formation',
      header: 'Formation',
      meta: { type: 'text' },
    },
    ...numericFields.map(({ key, header }) => ({
      id: key,
      accessorKey: key,
      header,
      meta: {
        type: 'integer' as const,
        align: 'right' as const,
      },
    })),
  ];
};

// ─────────────────────────────────────
// Column Definitions (expanded sub-table) - dynamic based on toggle
// ─────────────────────────────────────

const getSubTableColumns = (
  showExpiredPermits: boolean
): DataTableColumnDef<SlotFormation>[] => {
  const numericFields = getNumericFields(showExpiredPermits);
  return [
    {
      id: '_spacer',
      header: '',
      size: 48,
      minSize: 48,
      maxSize: 48,
      enableSorting: false,
      enableResizing: false,
      cell: () => null,
    },
    {
      id: 'formation',
      accessorKey: 'formation',
      header: 'Formation',
      meta: { type: 'text' },
    },
    ...numericFields.map(({ key, header }) => ({
      id: key,
      accessorKey: key,
      header,
      meta: {
        type: 'integer' as const,
        align: 'right' as const,
      },
    })),
  ];
};

/**
 * Transform headers for export - adds "(Excluded)" suffix to Expired Permit column.
 *
 * @param {string} columnId - The column ID
 * @param {string} displayHeader - The display header from column definition
 * @return {string} The transformed header for export
 */
const transformExportHeader = (columnId: string, displayHeader: string) => {
  if (columnId === 'expiredPermit') {
    return 'Expired Permit (Excluded)';
  }
  return displayHeader;
};

const FormationSubTable = ({
  formations,
  showExpiredPermits,
}: {
  formations: SlotFormation[];
  showExpiredPermits: boolean;
}) => {
  const subTableColumns = useMemo(
    () => getSubTableColumns(showExpiredPermits),
    [showExpiredPermits]
  );

  return (
    <DataTable<SlotFormation>
      data={formations}
      columns={subTableColumns}
      getRowId={(row) => row.formation}
      config={{
        sorting: false,
        columnResizing: false,
        columnReordering: false,
        columnPinning: false,
        columnVisibility: false,
        globalSearch: false,
        columnFilters: false,
        export: false,
        hideHeader: true,
        hideFooter: true,
      }}
    />
  );
};

export const SlotFormationTable = ({
  horizontalSlotsByFormation,
  isLoading,
  tableId,
}: {
  horizontalSlotsByFormation?: Partial<SlotsByFormationResponse>;
  isLoading: boolean;
  /** Unique table identifier for localStorage key */
  tableId: string;
}) => {
  // "Show Expired Permits" toggle state (INS-7119)
  const { showExpiredPermits, setShowExpiredPermits } =
    useShowExpiredPermitsFormation(tableId);

  const formationDataAvailable =
    horizontalSlotsByFormation?.formationDataAvailable ?? true;
  const summaryTotals = horizontalSlotsByFormation?.summaryTotals;
  const totalRow = horizontalSlotsByFormation?.totalRow;

  const basins: SlotsByFormationBasin[] = useMemo(() => {
    return horizontalSlotsByFormation?.basins ?? [];
  }, [horizontalSlotsByFormation?.basins]);

  // Dynamic columns based on toggle state
  const columns = useMemo(
    () => getColumns(showExpiredPermits),
    [showExpiredPermits]
  );

  // Render row sub-component with toggle state
  const renderRowSubComponent = (
    row: Row<SlotFormationRow>
  ): React.ReactNode => {
    const { _formations } = row.original;

    return (
      <FormationSubTable
        formations={_formations ?? []}
        showExpiredPermits={showExpiredPermits}
      />
    );
  };

  // ─── Build data rows for DataTable ───
  const data: SlotFormationRow[] = useMemo(() => {
    const rows: SlotFormationRow[] = [];

    // When formation data is unavailable, show single summary row
    if (!formationDataAvailable && summaryTotals) {
      rows.push({
        _rowType: 'total',
        _basinName: '',
        formation: 'No Formation Available',
        pdp: summaryTotals.pdp,
        duc: summaryTotals.duc,
        permit: summaryTotals.permit,
        activeEvalLTD: summaryTotals.activeEvalLTD,
        valuedSlotTotal: summaryTotals.valuedSlotTotal,
        activeUpsideLTD: summaryTotals.activeUpsideLTD,
        shutIn: summaryTotals.shutIn,
        inactive: summaryTotals.inactive,
        ...wellStatusCounts(summaryTotals),
        expiredPermit: summaryTotals.expiredPermit,
        total: summaryTotals.total,
      });
      return rows;
    }

    // Basin rows — each carries its formations for the sub-component
    for (const basinGroup of basins) {
      const basinTotals = calculateBasinTotals(basinGroup.rows);

      rows.push({
        _rowType: 'basin',
        _basinName: basinGroup.basin,
        _formations: basinGroup.rows as SlotFormation[],
        formation: formatBasinName(basinGroup.basin),
        pdp: basinTotals.pdp,
        duc: basinTotals.duc,
        permit: basinTotals.permit,
        activeEvalLTD: basinTotals.activeEvalLTD,
        valuedSlotTotal: basinTotals.valuedSlotTotal,
        activeUpsideLTD: basinTotals.activeUpsideLTD,
        shutIn: basinTotals.shutIn,
        inactive: basinTotals.inactive,
        ...wellStatusCounts(basinTotals),
        expiredPermit: basinTotals.expiredPermit,
        total: basinTotals.total,
      });
    }

    // Total Slots row (not expandable — no _formations)
    if (totalRow) {
      rows.push({
        _rowType: 'total',
        _basinName: '__total__',
        formation: 'Total Slots',
        pdp: totalRow.pdp ?? 0,
        duc: totalRow.duc ?? 0,
        permit: totalRow.permit ?? 0,
        activeEvalLTD: totalRow.activeEvalLTD ?? 0,
        valuedSlotTotal: totalRow.valuedSlotTotal ?? 0,
        activeUpsideLTD: totalRow.activeUpsideLTD ?? 0,
        shutIn: totalRow.shutIn ?? 0,
        inactive: totalRow.inactive ?? 0,
        ...wellStatusCounts(totalRow),
        expiredPermit: totalRow.expiredPermit ?? 0,
        total: totalRow.total ?? 0,
      });
    }

    return rows;
  }, [basins, formationDataAvailable, summaryTotals, totalRow]);

  return (
    <div className={styles.tableContainer}>
      <div className={styles.toolbar}>
        <div className={styles.toggleSection}>
          <Toggle
            isActive={showExpiredPermits}
            handleToggle={setShowExpiredPermits}
          />
          <span className={styles.toggleLabel}>Show Expired Permits</span>
        </div>
      </div>
      <DataTable<SlotFormationRow>
        data={data}
        columns={columns}
        getRowId={(row) => `${row._rowType}-${row._basinName}-${row.formation}`}
        isLoading={isLoading}
        renderRowSubComponent={renderRowSubComponent}
        getRowCanExpand={(row) => row.original._rowType === 'basin'}
        config={{
          sorting: true,
          columnResizing: true,
          columnReordering: true,
          columnPinning: true,
          columnVisibility: true,
          globalSearch: true,
          columnFilters: true,
          export: {
            exportPresets: [
              {
                name: 'Export',
                format: 'csv',
                filename: 'horizontal-slots-by-formation',
                transformHeader: transformExportHeader,
              },
            ],
          },
        }}
      />
    </div>
  );
};
